import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardList, Plus, RefreshCw, Settings } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiFetch } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";

interface StocktakeRow {
  id: string;
  code: string;
  status: "draft" | "completed" | "cancelled";
  approvalStatus: "not_required" | "pending" | "approved" | "rejected";
  notes: string | null;
  createdAt: string;
  completedAt: string | null;
  createdByName: string | null;
}

interface StocktakeLine {
  id: string;
  materialCode: string;
  materialName: string;
  unit: string;
  bookQuantity: number;
  actualQuantity: number | null;
  note: string | null;
}

interface StocktakeDetail extends StocktakeRow {
  lines: StocktakeLine[];
}

export function StocktakesPage() {
  const queryClient = useQueryClient();
  const [detailId, setDetailId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [actuals, setActuals] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [requireApproval, setRequireApproval] = useState(false);

  const listQuery = useQuery({
    queryKey: ["stocktakes"],
    queryFn: () => apiFetch<StocktakeRow[]>("/api/v1/stocktakes"),
  });
  const settingsQuery = useQuery({
    queryKey: ["stocktake-settings"],
    queryFn: () => apiFetch<{ requireApproval: boolean }>("/api/v1/admin/settings/stocktake"),
  });
  const detailQuery = useQuery({
    queryKey: ["stocktake", detailId],
    queryFn: () => apiFetch<StocktakeDetail>(`/api/v1/stocktakes/${detailId}`),
    enabled: Boolean(detailId),
  });

  async function createStocktake() {
    setBusy(true);
    try {
      const created = await apiFetch<{ id: string }>("/api/v1/stocktakes", {
        method: "POST",
        body: JSON.stringify({ notes: notes || null }),
      });
      await queryClient.invalidateQueries({ queryKey: ["stocktakes"] });
      setCreateOpen(false);
      setNotes("");
      setActuals({});
      setDetailId(created.id);
      toast.success("盘点单已创建");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "创建失败");
    } finally {
      setBusy(false);
    }
  }

  async function saveLine(line: StocktakeLine) {
    if (!detailId) return;
    const value = actuals[line.id];
    if (value === undefined || value === "") return toast.error("请输入实盘数量");
    try {
      await apiFetch(`/api/v1/stocktakes/${detailId}/lines/${line.id}`, {
        method: "PATCH",
        body: JSON.stringify({ actualQuantity: Number(value) }),
      });
      await queryClient.invalidateQueries({ queryKey: ["stocktake", detailId] });
      toast.success("已保存");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "保存失败");
    }
  }

  async function complete() {
    if (!detailId) return;
    setBusy(true);
    try {
      const result = await apiFetch<{ changed: number; lines: number }>(
        `/api/v1/stocktakes/${detailId}/complete`,
        { method: "POST" },
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["stocktakes"] }),
        queryClient.invalidateQueries({ queryKey: ["stocktake", detailId] }),
        queryClient.invalidateQueries({ queryKey: ["consumables"] }),
        queryClient.invalidateQueries({ queryKey: ["inventory"] }),
      ]);
      toast.success(`盘点完成：${result.lines} 行，调整 ${result.changed} 行`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "完成盘点失败");
    } finally {
      setBusy(false);
    }
  }

  const detail = detailQuery.data;
  const editable = detail?.status === "draft";

  return (
    <div className="space-y-6">
      <PageHeader
        title="库存盘点"
        description="按当前耗材账面数量生成盘点单；录入实盘数量后完成，差异自动写库存流水。"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => listQuery.refetch()}
              disabled={listQuery.isFetching}
            >
              <RefreshCw className="h-4 w-4" />
              刷新
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setRequireApproval(settingsQuery.data?.requireApproval ?? false);
                setSettingsOpen(true);
              }}
            >
              <Settings className="h-4 w-4" />
              盘点设置
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              新建盘点
            </Button>
          </>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>盘点单</CardTitle>
          <CardDescription>最近 50 张；完成后的盘点不可修改。</CardDescription>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (listQuery.data ?? []).length === 0 ? (
            <EmptyState
              title="还没有盘点单"
              description="创建盘点单后，系统会把当前耗材库存带入。"
              action={{ label: "新建盘点", to: "/stocktakes" }}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>编号</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>创建人</TableHead>
                  <TableHead>创建时间</TableHead>
                  <TableHead>完成时间</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(listQuery.data ?? []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-xs">{row.code}</TableCell>
                    <TableCell>
                      <Badge variant={row.status === "completed" ? "success" : "warning"}>
                        {row.status === "completed"
                          ? "已完成"
                          : row.status === "draft"
                            ? "草稿"
                            : "已取消"}
                      </Badge>
                    </TableCell>
                    <TableCell>{row.createdByName ?? "—"}</TableCell>
                    <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                    <TableCell>{row.completedAt ? formatDateTime(row.completedAt) : "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setDetailId(row.id);
                          setActuals({});
                        }}
                      >
                        <ClipboardList className="h-3.5 w-3.5" />
                        查看
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>盘点设置</DialogTitle>
            <DialogDescription>控制盘点完成后是否需要独立审批。</DialogDescription>
          </DialogHeader>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={requireApproval}
              onChange={(event) => setRequireApproval(event.target.checked)}
            />
            启用盘点审批
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>
              取消
            </Button>
            <Button
              onClick={async () => {
                await apiFetch("/api/v1/admin/settings/stocktake", {
                  method: "PUT",
                  body: JSON.stringify({ requireApproval }),
                });
                await settingsQuery.refetch();
                setSettingsOpen(false);
              }}
            >
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新建盘点</DialogTitle>
            <DialogDescription>按当前耗材账面库存生成盘点行。</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>备注（可选）</Label>
            <Input
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="如：2026 年 Q4 季度盘点"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              取消
            </Button>
            <Button onClick={createStocktake} disabled={busy}>
              创建
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(detailId)} onOpenChange={(open) => !open && setDetailId(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>盘点单：{detail?.code ?? ""}</DialogTitle>
            <DialogDescription>
              {detail?.status === "completed"
                ? "已完成，差异已写库存流水。"
                : "逐行录入实盘数量并保存，全部录入后完成盘点。"}
            </DialogDescription>
          </DialogHeader>
          {detailQuery.isLoading ? (
            <Skeleton className="h-56 w-full" />
          ) : detail ? (
            <div className="max-h-[60vh] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>物料</TableHead>
                    <TableHead>账面</TableHead>
                    <TableHead>实盘</TableHead>
                    <TableHead>差异</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.lines.map((line) => {
                    const raw =
                      actuals[line.id] ??
                      (line.actualQuantity === null ? "" : String(line.actualQuantity));
                    const actualNumber = raw === "" ? null : Number(raw);
                    return (
                      <TableRow key={line.id}>
                        <TableCell>
                          {line.materialName}
                          <span className="ml-2 font-mono text-xs text-muted-foreground">
                            {line.materialCode}
                          </span>
                        </TableCell>
                        <TableCell>
                          {line.bookQuantity} {line.unit}
                        </TableCell>
                        <TableCell>
                          <Input
                            className="w-24"
                            type="number"
                            min={0}
                            disabled={!editable}
                            value={raw}
                            onChange={(event) =>
                              setActuals({ ...actuals, [line.id]: event.target.value })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {actualNumber === null
                            ? "—"
                            : actualNumber - line.bookQuantity > 0
                              ? `+${actualNumber - line.bookQuantity}`
                              : actualNumber - line.bookQuantity}
                        </TableCell>
                        <TableCell className="text-right">
                          {editable ? (
                            <Button variant="ghost" size="sm" onClick={() => saveLine(line)}>
                              保存
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailId(null)}>
              关闭
            </Button>
            {editable ? (
              <Button onClick={complete} disabled={busy}>
                完成盘点
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
